import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { sendMagicLink } from '../../lib/auth/sendMagicLink';
import { useResendCooldown } from '../../lib/auth/useResendCooldown';
import { MagicLinkSent } from './MagicLinkSent';

/**
 * Magic-link sign-in flow: form → explicit “email sent” confirmation.
 * Owns the auth-column heading so success can replace “Welcome back”.
 */
export function MagicLinkSignIn() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resendCooldown = useResendCooldown();

  const submitEmail = async (address: string) => {
    setSending(true);
    setError(null);
    const result = await sendMagicLink(address.trim());
    setSending(false);

    if (!result.ok) {
      setError(result.message);
      return;
    }

    resendCooldown.start();
    setSentTo(address.trim());
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    await submitEmail(email);
  };

  if (sentTo) {
    return (
      <MagicLinkSent
        email={sentTo}
        resending={sending}
        resendSecondsLeft={resendCooldown.secondsLeft}
        error={error}
        onResend={() => {
          void submitEmail(sentTo);
        }}
        onUseDifferentEmail={() => {
          setSentTo(null);
          setError(null);
          setEmail(sentTo);
        }}
      />
    );
  }

  return (
    <>
      <div className="text-center">
        <h2 className="text-[2rem] font-semibold tracking-[-0.03em] text-[#12141a]">
          {t('signIn.title')}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-[#12141a]/55">{t('signIn.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="mt-10 space-y-5">
        <label className="block text-sm font-semibold text-ink">
          {t('signIn.emailLabel')}
          <span className="relative mt-2 block" dir="ltr">
            <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink/35">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                />
              </svg>
            </span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-black/12 bg-white py-2.5 ps-10 pe-3 text-sm text-[#12141a] outline-none transition focus:border-[#c45c26]/50 focus:ring-2 focus:ring-[#c45c26]/20"
              placeholder={t('signIn.emailPlaceholder')}
            />
          </span>
        </label>

        <button
          type="submit"
          disabled={sending}
          className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition disabled:opacity-60"
          style={{ backgroundColor: '#c45c26' }}
        >
          {sending ? t('signIn.sending') : t('signIn.submit')}
        </button>

        {error ? (
          <p className="text-center text-sm text-red-700" role="alert">
            {error}
          </p>
        ) : (
          <p className="text-center text-sm text-ink/45">
            {t('signIn.noPassword')}
          </p>
        )}
      </form>
    </>
  );
}
